import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './lib/auth';
import { Toaster } from './components/ui/sonner';
import Layout from './components/Layout';
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import PlansPage from './pages/PlansPage';
import BrowsePoolsPage from './pages/BrowsePoolsPage';
import PoolDirectory from './pages/PoolDirectory';
import PoolDetail from './pages/PoolDetail';
import JoinPool from './pages/JoinPool';
import CreatePool from './pages/CreatePool';
import AdminPanel from './pages/AdminPanel';
import MyDashboard from './pages/MyDashboard';
import ContactPage from './pages/ContactPage';
import WidgetDemo from './pages/WidgetDemo';
import InvitePage from './pages/InvitePage';
import AuthCallback from './pages/AuthCallback';
import './App.css';

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="flex items-center justify-center h-screen"><div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" /></div>;
  if (!user) return <Navigate to="/" />;
  return children;
}

function RegisterRedirect() {
  const intent = localStorage.getItem('pfc_intent');
  const selectedPlan = localStorage.getItem('pfc_selected_plan');
  const selectedPool = localStorage.getItem('pfc_selected_pool');
  localStorage.removeItem('pfc_intent');
  localStorage.removeItem('pfc_selected_plan');
  localStorage.removeItem('pfc_selected_pool');
  if (intent === 'start_pool') {
    return <Navigate to={`/create-pool${selectedPlan ? `?plan=${selectedPlan}` : ''}`} />;
  }
  if (intent === 'join_pool' && selectedPool) {
    return <Navigate to={`/pools/${selectedPool}/join`} />;
  }
  return <Navigate to="/pools" />;
}

function AppRoutes() {
  const { user, loading } = useAuth();
  const location = useLocation();

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  // Handle OAuth callback synchronously before any route renders
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  if (loading) return <div className="flex items-center justify-center h-screen"><div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" /></div>;

  return (
    <Routes>
      {/* Public routes */}
      <Route path="/" element={user ? <Navigate to="/pools" /> : <LandingPage />} />
      <Route path="/login" element={user ? <Navigate to="/pools" /> : <LoginPage />} />
      <Route path="/register" element={user ? <Navigate to="/pools" /> : <RegisterPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/plans" element={user ? <Navigate to="/create-pool" /> : <PlansPage />} />
      <Route path="/browse" element={<BrowsePoolsPage />} />
      <Route path="/invite/:code" element={<InvitePage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/widgets" element={<WidgetDemo />} />

      {/* Protected routes */}
      <Route element={<ProtectedRoute><Layout /></ProtectedRoute>}>
        <Route path="/pools" element={<PoolDirectory />} />
        <Route path="/dashboard" element={<MyDashboard />} />
        <Route path="/pools/:poolId" element={<PoolDetail />} />
        <Route path="/pools/:poolId/join" element={<JoinPool />} />
        <Route path="/create-pool" element={<CreatePool />} />
        <Route path="/admin" element={<AdminPanel />} />
      </Route>

      {/* Catch-all */}
      <Route path="*" element={user ? <Navigate to="/pools" /> : <Navigate to="/" />} />
    </Routes>
  );
}

export default function App() {
  return (
    <Router>
      <AuthProvider>
        <AppRoutes />
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </Router>
  );
}
